import { useRef, useCallback, useState, useEffect } from "react";
import Mp3ControllerContext from "../contexts/Mp3ControllerContext";

export function Mp3ControllerProvider({ children }) {
  const players = useRef(new Map());
  const currentId = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [audioAnalyzer, setAudioAnalyzer] = useState(null);
  const sourceMap = useRef(new Map()); // id => { source, analyser, audioContext }

  const register = useCallback((id, audio) => {
    players.current.set(id, audio);
    currentId.current = id;
    setIsEnded(false);
  }, []);

  const unregister = useCallback((id) => {
    players.current.delete(id);
    if (currentId.current === id) {
      currentId.current = null;
    }
  }, []);

  const play = useCallback((id) => {
    for (const [pid, player] of players.current.entries()) {
      if (pid !== id) {
        player.pause();
        setIsPlaying(false);
        setAudioAnalyzer(null);
      }
    }
    const target = players.current.get(id);
    if (!target) return;

    currentId.current = id;
    target.play();

    let entry = sourceMap.current.get(id);
    if (!entry) {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaElementSource(target);
      const analyzer = audioContext.createAnalyser();

      source.connect(analyzer);
      analyzer.connect(audioContext.destination);

      entry = { audioContext, source, analyzer };
      sourceMap.current.set(id, entry);
    }

    setAudioAnalyzer(entry.analyzer);
    setIsPlaying(true);
    setIsEnded(false);
  }, []);

  const pause = useCallback(() => {
    const current = players.current.get(currentId.current);
    if (current) {
      current.pause();
      setIsPlaying(false);
      setAudioAnalyzer(null);
    }
  }, []);

  const handleEnded = useCallback((id) => {
    const current = players.current.get(id);
    if (current) {
      current.pause();
      setIsPlaying(false);
      setIsEnded(true);
      currentId.current = null;
      setAudioAnalyzer(null);
    }
  }, []);

  const getCurrentAudio = useCallback(() => {
    return currentId.current ? players.current.get(currentId.current) : null;
  }, []);

  const playLastAudio = useCallback(() => {
    play(currentId.current);
  }, [play]);

  const value = {
    register,
    unregister,
    play,
    pause,
    handleEnded,
    getCurrentAudio,
    playLastAudio,
    isPlaying,
    isEnded,
    audioAnalyzer,
  };

  return (
    <Mp3ControllerContext.Provider value={value}>
      {children}
    </Mp3ControllerContext.Provider>
  );
}
